import React, { useCallback, useEffect, useState } from 'react';
import { FaCheck, FaChevronDown, FaChevronRight, FaEdit, FaKey, FaListUl, FaPlus, FaTimes, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import ErpTemplate from '../../components/ErpTemplate';
import ReactSelect from '../../components/ReactSelect';
import { FinCheck, FinChecks, FinField, FinInput } from '../../components/FinField';
import { PopoverObservacao } from '../../components/PillObservacao';
import api from '../../services/api';
import notify from '../../services/notify';
import { listarRotasRestritas, PERMISSOES } from '../../util/acessoPermissao';
import '../../css/financeiro-parcela-pagar.css';

const TIPOS = { usuario: 'Usuário', perfil: 'Perfil' };
const ABAS = {
    acessos: { label: 'Acessos', subtitulo: 'Quem abre as telas e os endpoints restritos, além do admin.' },
    regras: { label: 'Regras', subtitulo: 'As telas e os endpoints restritos que cada regra abre.' },
};

// A tela ou o endpoint que não declara grupo fica neste, no fim da lista.
const SEM_GRUPO = 'Sem grupo';

const contar = (n, um, varios) => (n === 1 ? '1 ' + um : (n || 0) + ' ' + varios);
const emUso = (n) => contar(n, 'acesso', 'acessos');
const resumo = (p) => {
    const partes = [];
    if (p.rotas?.length) partes.push(contar(p.rotas.length, 'tela', 'telas'));
    if (p.endpoints?.length) partes.push(contar(p.endpoints.length, 'endpoint', 'endpoints'));
    return partes.length ? ' (' + partes.join(', ') + ')' : '';
};

/**
 * Permissões de acesso às telas (RestrictedRoute) e aos endpoints (middleware `restrito` da API) restritos. Em Acessos,
 * o admin dá a um usuário ou a um perfil as regras do catálogo; o de perfil vale para quem tem o perfil em qualquer
 * tenant. Em Regras, cadastra as regras: o nome, o código do enum (opcional) e as rotas que cada uma abre.
 */
export default function PainelAcesso() {
    const token = localStorage.getItem('token');
    const [aba, setAba] = useState('acessos');
    const [acessos, setAcessos] = useState([]);
    const [permissoes, setPermissoes] = useState([]);
    const [endpointsApi, setEndpointsApi] = useState([]);
    const [carregando, setCarregando] = useState(true);
    // { tipo, alvo: { value, label } | null, marcadas: [id], editando }
    const [modal, setModal] = useState(null);
    // { id | null, nome, codigo | null, codigoProtegido, rotas: [path], endpoints: [{ uri, metodos }], escolhendo, busca, fechados: [grupo] }
    const [regra, setRegra] = useState(null);
    // { tipo: 'acesso' | 'regra', item }
    const [remover, setRemover] = useState(null);
    const [gravando, setGravando] = useState(false);

    // O que uma regra pode abrir: as telas restritas do routes.js e os endpoints restritos da API. O endpoint de tela
    // fica no grupo dela; o que não é de tela nenhuma, no "Sem grupo".
    const telas = listarRotasRestritas();
    const catalogo = [
        ...telas.map(t => ({ chave: 'tela ' + t.rota, tipo: 'tela', grupo: t.grupo || SEM_GRUPO, nome: t.nome, rota: t.rota })),
        ...endpointsApi.map(e => ({
            chave: 'endpoint ' + e.uri, tipo: 'endpoint', nome: e.nome, rota: e.uri, metodos: e.metodos, telas: e.telas ?? [],
            grupo: telas.find(t => (e.telas ?? []).includes(t.rota))?.grupo || SEM_GRUPO,
        })),
    ];
    const gruposDoCatalogo = [...new Set(catalogo.map(i => i.grupo))];
    const nomeDaTela = (rota) => telas.find(t => t.rota === rota)?.nome ?? rota;
    const nomeDoEndpoint = (uri) => endpointsApi.find(e => e.uri === uri)?.nome ?? uri;

    const carregar = useCallback(() => {
        setCarregando(true);
        Promise.all([
            api({ method: 'get', action: '/acesso', token }),
            api({ method: 'get', action: '/acesso/permissoes', token }),
            api({ method: 'get', action: '/acesso/endpoints', token }),
        ])
            .then(([a, p, e]) => {
                setAcessos(a.data.data ?? []);
                setPermissoes(p.data.data ?? []);
                setEndpointsApi(e.data.data ?? []);
            })
            .catch(err => notify.apiError(err, 'Erro ao carregar as permissões de acesso'))
            .finally(() => setCarregando(false));
    }, [token]);

    useEffect(() => { carregar(); }, [carregar]);

    // A mesma busca de usuário do Log de Auditoria: só pessoa ativa.
    const buscarUsuarios = (input) => api({
        method: 'get', action: '/participante', token,
        params: { nome: input, tipoCadastro: 'pessoa', situacaoRegistro: 'ativo' },
    }).then(res => (res.data.data || []).map(r => ({ value: String(r.id), label: (r.codigo ? r.codigo + ' - ' : '') + r.nome })));

    const buscarPerfis = (input) => api({ method: 'get', action: '/rhPerfil', token, params: { nome: input } })
        .then(res => (res.data.data || []).map(r => ({ value: String(r.id), label: r.nome })));

    // Editar abre o acesso como está; o "para" e quem recebe ficam fixos.
    const abrir = (a) => setModal(a
        ? { tipo: a.tipo, alvo: { value: a.acessavel, label: a.nome }, marcadas: a.permissoes.map(p => p.id), editando: true }
        : { tipo: 'usuario', alvo: null, marcadas: [], editando: false });

    const alternar = (id) => setModal(m => ({
        ...m, marcadas: m.marcadas.includes(id) ? m.marcadas.filter(x => x !== id) : [...m.marcadas, id],
    }));

    const salvar = () => {
        setGravando(true);
        api({ method: 'post', action: '/acesso', token, data: { tipo: modal.tipo, id: modal.alvo.value, permissoes: modal.marcadas } })
            .then(() => {
                toast.success(modal.marcadas.length ? 'Acesso salvo' : 'Acesso removido');
                setModal(null);
                carregar();
            })
            .catch(err => notify.apiError(err, 'Erro ao salvar o acesso'))
            .finally(() => setGravando(false));
    };

    // Editar abre a regra como está, na lista das rotas dela.
    const abrirRegra = (p) => setRegra({
        id: p?.id ?? null, nome: p?.nome ?? '', codigo: p?.codigo ?? null,
        // Protege o código já salvo; escolher um código numa regra nova não trava o formulário.
        codigoProtegido: !!p?.id && Object.values(PERMISSOES).includes(p.codigo),
        rotas: p?.rotas ?? [], endpoints: p?.endpoints ?? [], escolhendo: false, busca: '', fechados: [],
    });

    const marcado = (item) => (item.tipo === 'tela'
        ? regra.rotas.includes(item.rota)
        : regra.endpoints.some(e => e.uri === item.rota));

    // A tela só agrupa: o endpoint vale pelo que está na regra. Marcar telas marca junto os endpoints delas, com todos
    // os métodos; desmarcar tira os que nenhuma outra tela marcada chama. Cada endpoint segue livre para desmarcar ou
    // mudar os métodos.
    const comTelas = (r, rotas, ligar) => {
        const novas = ligar ? [...r.rotas, ...rotas.filter(x => !r.rotas.includes(x))] : r.rotas.filter(x => !rotas.includes(x));
        const uris = [...new Set(catalogo.filter(i => i.tipo === 'endpoint' && i.telas.some(t => rotas.includes(t))).map(i => i.rota))];
        const endpoints = ligar
            ? [...r.endpoints, ...uris.filter(u => !r.endpoints.some(e => e.uri === u)).map(uri => ({ uri, metodos: [] }))]
            : r.endpoints.filter(e => !uris.includes(e.uri)
                || catalogo.some(i => i.tipo === 'endpoint' && i.rota === e.uri && i.telas.some(t => novas.includes(t))));
        return { ...r, rotas: novas, endpoints };
    };

    // O check da linha liga ou desliga a rota inteira: a tela leva os endpoints dela, e o endpoint entra com todos os
    // métodos.
    const alternarItem = (item) => setRegra(r => (item.tipo === 'tela'
        ? comTelas(r, [item.rota], !r.rotas.includes(item.rota))
        : {
            ...r, endpoints: r.endpoints.some(e => e.uri === item.rota)
                ? r.endpoints.filter(e => e.uri !== item.rota)
                : [...r.endpoints, { uri: item.rota, metodos: [] }],
        }));

    // Um método do endpoint. Vazio vale todos: marcar o último que faltava volta a vazio, e desmarcar o último tira o
    // endpoint da regra.
    const alternarMetodo = (item, metodo) => setRegra(r => {
        const todos = item.metodos ?? [];
        const atual = r.endpoints.find(e => e.uri === item.rota);
        const ligados = !atual ? [] : (atual.metodos.length ? atual.metodos : todos);
        const novos = ligados.includes(metodo) ? ligados.filter(m => m !== metodo) : [...ligados, metodo];
        const outros = r.endpoints.filter(e => e.uri !== item.rota);
        if (!novos.length) return { ...r, endpoints: outros };
        return { ...r, endpoints: [...outros, { uri: item.rota, metodos: todos.every(m => novos.includes(m)) ? [] : novos }] };
    });

    const metodosDe = (item) => {
        const atual = regra.endpoints.find(e => e.uri === item.rota);
        if (!atual) return (item.metodos ?? []).join(', ');
        return atual.metodos.length ? atual.metodos.join(', ') : 'todos os métodos';
    };

    // A lista padrão mostra as rotas da regra, inclusive as que saíram do código (para poder tirar); o `+ Rotas` mostra
    // todas, com a busca.
    const linhasDaRegra = () => {
        if (!regra.escolhendo) {
            const fora = [
                ...regra.rotas.filter(rota => !catalogo.some(i => i.tipo === 'tela' && i.rota === rota))
                    .map(rota => ({ chave: 'tela ' + rota, tipo: 'tela', grupo: SEM_GRUPO, nome: rota, rota })),
                ...regra.endpoints.filter(e => !catalogo.some(i => i.tipo === 'endpoint' && i.rota === e.uri))
                    .map(e => ({ chave: 'endpoint ' + e.uri, tipo: 'endpoint', grupo: SEM_GRUPO, nome: e.uri, rota: e.uri, metodos: e.metodos, telas: [] })),
            ];
            return [...catalogo.filter(marcado), ...fora];
        }
        const termo = regra.busca.trim().toLowerCase();
        return catalogo.filter(item => !termo
            || [item.nome, item.rota, item.grupo, item.tipo === 'tela' ? 'tela' : 'endpoint'].some(t => t.toLowerCase().includes(termo)));
    };

    // Dentro do grupo, cada tela seguida dos endpoints que ela chama (com recuo); o endpoint de várias telas aparece
    // embaixo de cada uma. O endpoint que fica sem a tela na lista (a busca achou só ele, ou foi dado à parte) vem no fim.
    const emArvore = (itens) => {
        const telasDoGrupo = itens.filter(i => i.tipo === 'tela');
        const endpoints = itens.filter(i => i.tipo === 'endpoint');
        const linhas = [];
        telasDoGrupo.forEach(t => {
            linhas.push({ item: t, filho: false, chave: t.chave });
            endpoints.filter(e => e.telas.includes(t.rota))
                .forEach(e => linhas.push({ item: e, filho: true, chave: t.chave + ' ' + e.chave }));
        });
        endpoints.filter(e => !e.telas.some(r => telasDoGrupo.some(t => t.rota === r)))
            .forEach(e => linhas.push({ item: e, filho: false, chave: e.chave }));
        return linhas;
    };

    // As linhas em acordeão, um por grupo, em ordem alfabética e o "Sem grupo" por último.
    const gruposDe = (itens) => Object.entries(itens.reduce((acc, item) => ({ ...acc, [item.grupo]: [...(acc[item.grupo] ?? []), item] }), {}))
        .sort(([a], [b]) => (a === SEM_GRUPO) - (b === SEM_GRUPO) || a.localeCompare(b));

    // Na busca, todo grupo abre; fora dela, abre e fecha pelo cabeçalho.
    const estaAberto = (grupo) => !!regra.busca.trim() || !regra.fechados.includes(grupo);
    const alternarAberto = (grupo) => setRegra(r => ({
        ...r, fechados: r.fechados.includes(grupo) ? r.fechados.filter(g => g !== grupo) : [...r.fechados, grupo],
    }));

    // O check do grupo marca ou desmarca tudo dele que está na lista: as telas, com os endpoints delas, e os endpoints.
    const alternarGrupo = (itens) => {
        const tirar = itens.every(marcado);
        const rotas = itens.filter(i => i.tipo === 'tela').map(i => i.rota);
        const uris = itens.filter(i => i.tipo === 'endpoint').map(i => i.rota);
        setRegra(r => {
            const s = comTelas(r, rotas, !tirar);
            return tirar
                ? { ...s, endpoints: s.endpoints.filter(e => !uris.includes(e.uri)) }
                : { ...s, endpoints: [...s.endpoints, ...uris.filter(u => !s.endpoints.some(e => e.uri === u)).map(uri => ({ uri, metodos: [] }))] };
        });
    };

    const salvarRegra = () => {
        setGravando(true);
        api({
            method: 'post', action: '/acesso/permissao', token, data: {
                id: regra.id, nome: regra.nome.trim(), codigo: regra.codigo, rotas: regra.rotas, endpoints: regra.endpoints,
            },
        })
            .then(() => {
                toast.success('Regra salva');
                setRegra(null);
                carregar();
            })
            .catch(err => notify.apiError(err, 'Erro ao salvar a regra'))
            .finally(() => setGravando(false));
    };

    const confirmarRemover = () => {
        const ehRegra = remover.tipo === 'regra';
        setGravando(true);
        api({ method: 'delete', action: (ehRegra ? '/acesso/permissao/' : '/acesso/') + remover.item.id, token })
            .then(() => {
                toast.success(ehRegra ? 'Regra excluída' : 'Acesso removido');
                setRemover(null);
                carregar();
            })
            .catch(err => notify.apiError(err, ehRegra ? 'Erro ao excluir a regra' : 'Erro ao remover o acesso'))
            .finally(() => setGravando(false));
    };

    // O código vem do enum do front; o que já está noutra regra não se escolhe de novo.
    const opcoesCodigo = Object.values(PERMISSOES).map(c => ({
        value: c, label: c, isDisabled: permissoes.some(p => p.codigo === c && p.id !== regra?.id),
    }));

    const linhas = regra ? linhasDaRegra() : [];

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Permissões de Acesso</h1>
                        <p className="fin-pp__subtitle">{ABAS[aba].subtitulo}</p>
                    </div>
                    <div className="fin-pp__actions">
                        {aba === 'acessos' ? (
                            <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--primary" onClick={() => abrir(null)}>
                                <FaPlus size={12} /> Liberar acesso
                            </button>
                        ) : (
                            <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--primary" onClick={() => abrirRegra(null)}>
                                <FaPlus size={12} /> Nova regra
                            </button>
                        )}
                    </div>
                </div>

                <div className="fin-pp__tabs mb-3">
                    {Object.entries(ABAS).map(([chave, a]) => (
                        <button key={chave} type="button" className={'fin-pp__tab' + (aba === chave ? ' is-active' : '')}
                            onClick={() => setAba(chave)}>
                            {a.label}
                        </button>
                    ))}
                </div>

                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        {aba === 'acessos' ? (
                            <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                <thead>
                                    <tr>
                                        <th>Para</th>
                                        <th>Regras</th>
                                        <th>Atualizado</th>
                                        <th style={{ width: 1 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {carregando ? (
                                        <tr><td colSpan={4} className="fin-pp__empty">Carregando...</td></tr>
                                    ) : acessos.length === 0 ? (
                                        <tr><td colSpan={4} className="fin-pp__empty">Nenhum acesso liberado.</td></tr>
                                    ) : acessos.map(a => (
                                        <tr key={a.id}>
                                            <td>
                                                <div className="fin-pp__cell-strong">{a.nome || '—'}</div>
                                                <div className="d-flex gap-2" style={{ marginTop: 4 }}>
                                                    <span className="fin-pp__pill is-info">{TIPOS[a.tipo]}</span>
                                                </div>
                                            </td>
                                            <td>{a.permissoes.map(p => p.nome).join(', ') || '—'}</td>
                                            <td className="fin-pp__cell-muted">
                                                {a.atualizadoEm ? moment(a.atualizadoEm).format('DD/MM/YYYY HH:mm') : '—'}
                                                {a.atualizadoPor ? ' · ' + a.atualizadoPor : ''}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2" style={{ whiteSpace: 'nowrap' }}>
                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => abrir(a)}>
                                                        <FaEdit size={11} /> Editar
                                                    </button>
                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                        title="Remover o acesso" onClick={() => setRemover({ tipo: 'acesso', item: a })}>
                                                        <FaTrash size={11} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                <thead>
                                    <tr>
                                        <th>Regra</th>
                                        <th>Rotas</th>
                                        <th>Em uso</th>
                                        <th style={{ width: 1 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {carregando ? (
                                        <tr><td colSpan={4} className="fin-pp__empty">Carregando...</td></tr>
                                    ) : permissoes.length === 0 ? (
                                        <tr><td colSpan={4} className="fin-pp__empty">Nenhuma regra cadastrada.</td></tr>
                                    ) : permissoes.map(p => (
                                        <tr key={p.id}>
                                            <td>
                                                <div className="fin-pp__cell-strong">{p.nome}</div>
                                                {p.codigo && (
                                                    <div className="d-flex gap-2" style={{ marginTop: 4 }}>
                                                        <span className="fin-pp__pill is-muted" title="O sistema confere esta regra pelo código: ela não sai enquanto tiver código">
                                                            {p.codigo}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {[
                                                    ...(p.rotas ?? []).map(nomeDaTela),
                                                    ...(p.endpoints ?? []).map(e => nomeDoEndpoint(e.uri) + (e.metodos?.length ? ' (' + e.metodos.join(', ') + ')' : '')),
                                                ].join(', ') || '—'}
                                            </td>
                                            <td className="fin-pp__cell-muted">{emUso(p.acessos_count)}</td>
                                            <td>
                                                <div className="d-flex gap-2" style={{ whiteSpace: 'nowrap' }}>
                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => abrirRegra(p)}>
                                                        <FaEdit size={11} /> Editar
                                                    </button>
                                                    {!p.codigo && (
                                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                            title="Excluir a regra" onClick={() => setRemover({ tipo: 'regra', item: p })}>
                                                            <FaTrash size={11} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {modal && (
                <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                    onClick={(e) => { if (e.target === e.currentTarget && !gravando) setModal(null); }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content fin-pp__modal fin-pp__modal--flow">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic"><FaKey size={17} /></span>
                                <h3 className="fin-pp__modal-title">{modal.editando ? 'Editar acesso' : 'Liberar acesso'}
                                    <span className="fin-pp__modal-sub">Cada regra abre as rotas dela. Nenhuma marcada remove o acesso.</span>
                                </h3>
                                <button type="button" className="fin-pp__modal-close" onClick={() => setModal(null)} disabled={gravando}>
                                    <FaTimes size={16} />
                                </button>
                            </div>
                            <div className="modal-body" style={{ minHeight: 260 }}>
                                <div className="fin-pp__filters">
                                    <FinField label="Para" required>
                                        <FinChecks inline>
                                            {Object.entries(TIPOS).map(([valor, rotulo]) => (
                                                <FinCheck key={valor} id={'acesso-tipo-' + valor} type="radio" name="acesso-tipo" label={rotulo}
                                                    checked={modal.tipo === valor} disabled={modal.editando}
                                                    onChange={() => setModal(m => ({ ...m, tipo: valor, alvo: null }))} />
                                            ))}
                                        </FinChecks>
                                    </FinField>
                                    <FinField label={TIPOS[modal.tipo]} required grow>
                                        <ReactSelect key={modal.tipo}
                                            defaultOptions={modal.tipo === 'perfil'}
                                            placeholder={modal.tipo === 'perfil' ? 'Escolha o perfil' : 'Buscar pelo nome...'}
                                            value={modal.alvo}
                                            isDisabled={modal.editando}
                                            loadOptions={modal.tipo === 'perfil' ? buscarPerfis : buscarUsuarios}
                                            onChange={(e) => setModal(m => ({ ...m, alvo: e || null }))} />
                                    </FinField>
                                    <FinField label="Regras" full>
                                        {permissoes.length === 0 ? (
                                            <span className="fin-pp__cell-muted">Nenhuma regra cadastrada.</span>
                                        ) : (
                                            <FinChecks>
                                                {permissoes.map(p => (
                                                    <FinCheck key={p.id} id={'acesso-permissao-' + p.id} label={p.nome + resumo(p)}
                                                        checked={modal.marcadas.includes(p.id)} onChange={() => alternar(p.id)} />
                                                ))}
                                            </FinChecks>
                                        )}
                                    </FinField>
                                </div>
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" onClick={() => setModal(null)} disabled={gravando}>Cancelar</button>
                                <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={salvar} disabled={gravando || !modal.alvo}>
                                    {gravando ? 'Salvando...' : 'Salvar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {regra && (
                <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                    onClick={(e) => { if (e.target === e.currentTarget && !gravando) setRegra(null); }}>
                    {/* Largo: cada linha da lista de rotas leva o nome, a rota e o tipo inteiros. */}
                    <div className="modal-dialog modal-dialog-centered modal-xl" style={{ minWidth: 'min(1100px, calc(100vw - 32px))' }}>
                        <div className="modal-content fin-pp__modal fin-pp__modal--flow">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic"><FaListUl size={17} /></span>
                                <h3 className="fin-pp__modal-title">{regra.id ? 'Editar regra' : 'Nova regra'}
                                    <span className="fin-pp__modal-sub">Quem recebe a regra em Acessos abre as telas e chama os endpoints dela.</span>
                                </h3>
                                <button type="button" className="fin-pp__modal-close" onClick={() => setRegra(null)} disabled={gravando}>
                                    <FaTimes size={16} />
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="fin-pp__filters">
                                    <FinField label="Nome" required grow>
                                        <FinInput value={regra.nome} maxLength={191}
                                            onChange={(e) => { const nome = e.target.value; setRegra(r => ({ ...r, nome })); }} />
                                    </FinField>
                                    <FinField label="Código">
                                        <ReactSelect options={opcoesCodigo} isClearable={!regra.codigoProtegido}
                                            isDisabled={regra.codigoProtegido} placeholder="Nenhum"
                                            value={regra.codigo ? { value: regra.codigo } : null}
                                            onChange={(e) => setRegra(r => ({ ...r, codigo: e ? e.value : null }))} />
                                    </FinField>
                                </div>

                                <div className="d-flex align-items-center gap-2" style={{ margin: '16px 0 8px' }}>
                                    <span className="fin-pp__label" style={{ margin: 0 }}>Rotas</span>
                                    <div style={{ flex: 1 }} />
                                    {regra.escolhendo ? (
                                        <>
                                            <FinInput placeholder="Busque uma rota, tela ou endpoint" value={regra.busca} autoFocus
                                                style={{ maxWidth: 320, height: 36 }}
                                                onChange={(e) => { const busca = e.target.value; setRegra(r => ({ ...r, busca })); }} />
                                            <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--primary"
                                                title="Voltar às rotas da regra" onClick={() => setRegra(r => ({ ...r, escolhendo: false, busca: '', fechados: [] }))}>
                                                <FaCheck size={11} />
                                            </button>
                                        </>
                                    ) : (
                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                            onClick={() => setRegra(r => ({ ...r, escolhendo: true, fechados: gruposDoCatalogo }))}>
                                            <FaPlus size={11} /> Rotas
                                        </button>
                                    )}
                                </div>

                                {/* A lista rola; cada grupo tem altura própria. Sem o flexShrink 0, a coluna espreme os grupos, que têm
                                    overflow hidden, e corta as linhas em vez de rolar. */}
                                <div className="d-flex flex-column gap-2" style={{ maxHeight: 380, overflowY: 'auto' }}>
                                    {linhas.length === 0 ? (
                                        <div className="fin-pp__card">
                                            <div className="fin-pp__empty">
                                                {regra.escolhendo ? 'Nenhuma rota encontrada.' : 'Nenhuma rota na regra. Use + Rotas para escolher.'}
                                            </div>
                                        </div>
                                    ) : gruposDe(linhas).map(([grupo, itens], g) => {
                                        const aberto = estaAberto(grupo);
                                        const marcados = itens.filter(marcado).length;
                                        return (
                                            <div key={grupo} className="fin-pp__grp" style={{ flexShrink: 0 }}>
                                                <div className="fin-pp__grp-head" onClick={() => alternarAberto(grupo)}>
                                                    <span className="fin-pp__grp-caret">{aberto ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}</span>
                                                    <span className="fin-pp__grp-name">{grupo}</span>
                                                    <span className="fin-pp__grp-badge">{marcados} de {itens.length}</span>
                                                    <span style={{ marginLeft: 'auto' }} title="Marcar ou desmarcar o grupo" onClick={(e) => e.stopPropagation()}>
                                                        <FinCheck id={'regra-grupo-' + g} checked={marcados === itens.length} onChange={() => alternarGrupo(itens)} />
                                                    </span>
                                                </div>
                                                {aberto && (
                                                    <div className="fin-pp__tblwrap">
                                                        <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Nome</th>
                                                                    <th>Rota</th>
                                                                    <th>Tipo</th>
                                                                    <th style={{ width: 1 }}></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {emArvore(itens).map(({ item, filho, chave }, i) => (
                                                                    <tr key={chave}>
                                                                        <td style={filho ? { paddingLeft: 36 } : undefined}>
                                                                            <div className="fin-pp__cell-strong">{item.nome}</div>
                                                                            {item.tipo === 'endpoint' && !filho && item.telas.length > 0 && (
                                                                                <div className="fin-pp__cell-muted" style={{ fontSize: 12 }}>
                                                                                    Telas: {item.telas.map(nomeDaTela).join(', ')}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td className="fin-pp__cell-muted" style={{ fontFamily: 'monospace' }}>{item.rota}</td>
                                                                        <td>
                                                                            {item.tipo === 'tela' ? 'Tela' : (
                                                                                <PopoverObservacao rotulo="Métodos" titulo="Escolher os métodos do endpoint"
                                                                                    conteudo={(
                                                                                        <div className="fin-pp">
                                                                                            <FinChecks>
                                                                                                {(item.metodos ?? []).map(m => {
                                                                                                    const atual = regra.endpoints.find(e => e.uri === item.rota);
                                                                                                    return (
                                                                                                        <FinCheck key={m} id={'regra-metodo-' + g + '-' + i + '-' + m} label={m}
                                                                                                            checked={!!atual && (!atual.metodos.length || atual.metodos.includes(m))}
                                                                                                            onChange={() => alternarMetodo(item, m)} />
                                                                                                    );
                                                                                                })}
                                                                                            </FinChecks>
                                                                                        </div>
                                                                                    )}>
                                                                                    Endpoint <span className="fin-pp__cell-muted">· {metodosDe(item)}</span> <FaChevronDown size={9} />
                                                                                </PopoverObservacao>
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            <FinCheck id={'regra-rota-' + g + '-' + i} checked={marcado(item)} onChange={() => alternarItem(item)} />
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" onClick={() => setRegra(null)} disabled={gravando}>Cancelar</button>
                                <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={salvarRegra} disabled={gravando || !regra.nome.trim()}>
                                    {gravando ? 'Salvando...' : 'Salvar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {remover && (
                <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                    onClick={(e) => { if (e.target === e.currentTarget && !gravando) setRemover(null); }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic is-danger"><FaTrash size={16} /></span>
                                {remover.tipo === 'regra' ? (
                                    <h3 className="fin-pp__modal-title">Excluir a regra?
                                        <span className="fin-pp__modal-sub">
                                            {remover.item.nome}: sai de {emUso(remover.item.acessos_count)}, e o acesso que ficar sem regra é removido.
                                        </span>
                                    </h3>
                                ) : (
                                    <h3 className="fin-pp__modal-title">Remover o acesso?
                                        <span className="fin-pp__modal-sub">
                                            {TIPOS[remover.item.tipo]}: {remover.item.nome || '—'}. As rotas restritas deixam de abrir por este acesso.
                                        </span>
                                    </h3>
                                )}
                                <button type="button" className="fin-pp__modal-close" onClick={() => setRemover(null)} disabled={gravando}>
                                    <FaTimes size={16} />
                                </button>
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" onClick={() => setRemover(null)} disabled={gravando}>Cancelar</button>
                                <button type="button" className="fin-pp__btn fin-pp__btn--danger" onClick={confirmarRemover} disabled={gravando}>
                                    {remover.tipo === 'regra'
                                        ? (gravando ? 'Excluindo...' : 'Excluir')
                                        : (gravando ? 'Removendo...' : 'Remover')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </ErpTemplate>
    );
}
